// React Compiler opt-out: this file still reads mutable external state during render
// (legacy store object getters and/or current-time reads) that the useFluxStore bridge
// doesn't cover. Remove after migrating those reads (see flux-bridge-conversion skill).
'use no memo';

import React from 'react';
import {renderStringOrSupplement} from './utils';
import {extractSupplementParts} from './utils';
import {Chip, Icon} from '@albert-io/atomic';
import classNames from 'classnames';
import {
  DraggableChip,
  getSmallSupplement,
  SmallSupplementContent,
  useEnsureSupplementLoaded,
  useIsSmallSupplementsEnabled
} from './DraggableChip';

interface MovableItemProps {
  item: {id: string; value: string; placedInSlot?: string | null};
  isActive: boolean;
  activeItemId?: string | null;
  onClick: (e: React.MouseEvent) => void;
  onReset?: (itemId: string) => void;
  isDragging?: boolean;
  disabled?: boolean;
  isSubmitted?: boolean;
  isCorrect?: boolean;
  showIcon?: boolean;
  /** When true, the item never dims because another item is being dragged/selected (e.g. it's already placed in an answer). */
  suppressDimming?: boolean;
}

export const MovableItem = ({
  item,
  activeItemId,
  isActive,
  onClick,
  onReset,
  isDragging,
  isSubmitted,
  isCorrect,
  showIcon = true,
  suppressDimming
}: MovableItemProps) => {
  const isSmallSupplementsEnabled = useIsSmallSupplementsEnabled();
  // The submitted view renders supplement content directly (not through SupplementChip), so ensure
  // the supplement is loaded before resolving small-vs-full — otherwise it briefly flashes full-size.
  const isSupplementReady = useEnsureSupplementLoaded(
    item.value,
    isSmallSupplementsEnabled && !!isSubmitted
  );
  const smallSupplement = isSmallSupplementsEnabled ? getSmallSupplement(item.value) : null;
  const parts = smallSupplement ? extractSupplementParts(item.value) : null;
  const surroundingText = parts?.text || '';
  const isSubmittedSupplementPending =
    isSmallSupplementsEnabled && !isSupplementReady && !!extractSupplementParts(item.value);

  const submittedContent = smallSupplement ? (
    <>
      {surroundingText && <span className='mr-x0-half'>{surroundingText}</span>}
      <SmallSupplementContent type={smallSupplement.type} supplement={smallSupplement.supplement} />
    </>
  ) : (
    renderStringOrSupplement(item.value)
  );

  // Only items the student placed/ordered are passed an explicit boolean `isCorrect`; an
  // item left unused in the bank gets none, so keep it neutral (no positive/negative
  // styling or correctness icon) instead of defaulting to the "incorrect" treatment.
  const hasCorrectness = typeof isCorrect === 'boolean';
  const submittedBgClass = !hasCorrectness
    ? 'background-primary'
    : isCorrect
      ? 'background-positive-subtle'
      : 'background-negative-subtle';

  return (
    <>
      {!isSubmitted ? (
        <DraggableChip
          id={item.id}
          value={item.value}
          isActive={isActive}
          isDragging={isDragging}
          activeItemId={activeItemId}
          onClick={onClick}
          onRemove={
            onReset
              ? (e) => {
                  e.stopPropagation();
                  onReset(item.id);
                }
              : undefined
          }
          suppressDimming={suppressDimming}
          size='small'
        />
      ) : isSubmittedSupplementPending ? (
        // Hold the supplement's eventual space while it loads instead of collapsing to
        // nothing, so switching attempts doesn't reflow the layout when content pops in.
        <div
          className='background-subtle min-h-[168px] w-full animate-pulse rounded-lg'
          aria-hidden={true}
        />
      ) : (
        <>
          {smallSupplement ? (
            <div
              className={classNames(
                'min-w-0 flex h-auto! items-center gap-x1 overflow-hidden rounded-lg border border-border-primary text-left font-normal',
                submittedBgClass
              )}
            >
              {showIcon && hasCorrectness && (
                <Icon
                  icon={isCorrect ? ['far', 'check-circle'] : ['far', 'circle-xmark']}
                  color={isCorrect ? 'positive' : 'negative'}
                  className='ml-x1 shrink-0'
                />
              )}
              {submittedContent}
            </div>
          ) : (
            <Chip
              color={!hasCorrectness ? 'neutral' : isCorrect ? 'positive' : 'negative'}
              size='small'
              noMargin
              className='border-border-primary min-w-0 overflow-hidden border object-contain'
            >
              {showIcon && hasCorrectness && (
                <Icon
                  icon={isCorrect ? ['far', 'check-circle'] : ['far', 'circle-xmark']}
                  className='mr-x0-half'
                />
              )}
              <span className='truncate'>{submittedContent}</span>
            </Chip>
          )}
        </>
      )}
    </>
  );
};
